import { useEffect, useState } from 'react'
import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Folder, FileText, FileImage, File, Search, Download, Home, ChevronRight } from 'lucide-react'

/** One file returned by the `student-resources` Edge Function. */
interface StudentFile {
  name: string
  title: string
  /** Folder path, e.g. ["IAS", "Prelims", "GS"]; empty at the top level. */
  path: string[]
  kind: 'pdf' | 'document' | 'image' | 'other'
  sizeBytes: number
  url: string
}

const KIND_TABS: { key: string | null; label: string }[] = [
  { key: null, label: 'All' },
  { key: 'pdf', label: 'PDFs' },
  { key: 'document', label: 'Documents' },
  { key: 'image', label: 'Images' },
]

function iconFor(kind: string) {
  if (kind === 'pdf') return FileText
  if (kind === 'image') return FileImage
  return File
}

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function StudentResources() {
  const [files, setFiles] = useState<StudentFile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [crumbs, setCrumbs] = useState<string[]>([])
  const [kind, setKind] = useState<string | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/student-resources`
    fetch(endpoint)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error)
        else setFiles(data.files || [])
      })
      .catch(() => setError('Could not load study material'))
      .finally(() => setLoading(false))
  }, [])

  const q = query.trim().toLowerCase()
  const byKind = files.filter((f) => !kind || f.kind === kind)

  // Searching looks through every folder; otherwise browse the current one.
  const searching = q.length > 0
  const hits = byKind.filter((f) => f.title.toLowerCase().includes(q))
  const here = byKind.filter((f) => f.path.slice(0, crumbs.length).join('/') === crumbs.join('/'))
  const folders = Array.from(
    new Set(here.filter((f) => f.path.length > crumbs.length).map((f) => f.path[crumbs.length])),
  ).map((name) => ({
    name,
    fileCount: here.filter((f) => f.path[crumbs.length] === name).length,
  }))
  const filesHere = here.filter((f) => f.path.length === crumbs.length)

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2">
            Student Resources
          </h1>
          <p className="font-inter text-muted-foreground text-lg">
            Notes, papers and study material — click any file to download
          </p>
        </div>

        {/* Type tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-5">
          {KIND_TABS.map((t) => (
            <button
              key={t.label}
              onClick={() => setKind(t.key)}
              className={`px-4 py-2 rounded-xl font-inter text-sm transition-all ${
                kind === t.key
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-card text-foreground hover:bg-primary/5 border'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="relative max-w-xl mx-auto mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all folders..."
            className="pl-9"
            data-testid="input-search-resources"
          />
        </div>

        {/* Breadcrumbs */}
        {!searching && crumbs.length > 0 && (
          <div className="flex items-center flex-wrap gap-1 mb-4 text-sm">
            <button
              onClick={() => setCrumbs([])}
              className="p-1 rounded hover:bg-accent text-primary"
              aria-label="All folders"
            >
              <Home className="w-4 h-4" />
            </button>
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                <button
                  onClick={() => setCrumbs(crumbs.slice(0, i + 1))}
                  disabled={i === crumbs.length - 1}
                  className={
                    i === crumbs.length - 1
                      ? 'font-semibold text-foreground'
                      : 'text-primary hover:underline'
                  }
                >
                  {c}
                </button>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : error ? (
          <p className="text-center text-destructive py-16">{error}</p>
        ) : searching ? (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {hits.length} {hits.length === 1 ? 'result' : 'results'} in all folders
            </p>
            <FileGrid files={hits} showPath />
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {[
                folders.length ? `${folders.length} folder${folders.length === 1 ? '' : 's'}` : null,
                filesHere.length ? `${filesHere.length} file${filesHere.length === 1 ? '' : 's'}` : null,
              ].filter(Boolean).join('  •  ') || 'Empty folder'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {folders.map((f) => (
                <Card
                  key={f.name}
                  onClick={() => setCrumbs([...crumbs, f.name])}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <Folder className="w-8 h-8 text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      <h3 className="font-inter font-semibold text-foreground truncate">{f.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {f.fileCount} file{f.fileCount === 1 ? '' : 's'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {filesHere.length > 0 && <div className="mt-3"><FileGrid files={filesHere} /></div>}
          </>
        )}
      </main>
      <Footer />
    </div>
  )
}

function FileGrid({ files, showPath = false }: { files: StudentFile[]; showPath?: boolean }) {
  if (files.length === 0) {
    return <p className="text-center text-muted-foreground py-16">No files found.</p>
  }
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {files.map((f) => {
        const Icon = iconFor(f.kind)
        return (
          <Card key={f.name} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex flex-col h-full">
              <div className="flex items-start gap-3 mb-3">
                <Icon className="w-7 h-7 text-primary flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="font-inter font-semibold text-foreground leading-snug">{f.title}</h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {[showPath ? f.path.join(' › ') : null, formatSize(f.sizeBytes)]
                      .filter(Boolean)
                      .join('  •  ')}
                  </p>
                </div>
              </div>
              <Button size="sm" className="mt-auto w-full" asChild>
                <a href={f.url} target="_blank" rel="noopener noreferrer">
                  Download <Download className="w-3.5 h-3.5 ml-1" />
                </a>
              </Button>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
